import {

  CognitoIdentityProviderClient,

  InitiateAuthCommand,

  RespondToAuthChallengeCommand,

  type ChallengeNameType,

} from '@aws-sdk/client-cognito-identity-provider';



export interface CognitoOtpConfig {

  region: string;

  userPoolId: string;

  clientId: string;

}



export interface OtpChallengeState {

  email: string;

  cognitoUsername?: string;

  session: string;

  challengeName: ChallengeNameType;

  accessToken?: string;

}



const OTP_UNAVAILABLE_MESSAGE =

  'OTP login unavailable (Cognito configuration). Use Sign up at app.noddpay.com or paste token (dev).';



function getErrorMessage(error: unknown): string {

  if (error instanceof Error) {

    if (import.meta.env.DEV) {

      console.error('[cognito-auth]', error.name, error.message);

    }



    const message = error.message;

    const lambdaFailure =

      error.name === 'UnexpectedLambdaException' ||

      message.includes('DefineAuthChallenge') ||

      message.includes('AccessDeniedException') ||

      message.includes('due to configuration');



    if (lambdaFailure) {

      return OTP_UNAVAILABLE_MESSAGE;

    }



    if (error.name === 'UserNotFoundException') {

      return 'No account for this email';

    }



    if (error.name === 'NotAuthorizedException') {

      return message.includes('Incorrect username or password')

        ? 'No account for this email'

        : 'Invalid email or verification code';

    }



    if (

      error.name === 'InvalidParameterException' &&

      message.includes('ANSWER')

    ) {

      return 'Verification code is required';

    }



    return message;

  }



  return 'Authentication failed';

}



export async function startOtpChallenge(

  config: CognitoOtpConfig,

  email: string

): Promise<OtpChallengeState> {

  const client = new CognitoIdentityProviderClient({ region: config.region });

  const username = email.trim().toLowerCase();



  const initiate = await client.send(

    new InitiateAuthCommand({

      AuthFlow: 'CUSTOM_AUTH',

      ClientId: config.clientId,

      AuthParameters: { USERNAME: username },

    })

  );



  if (initiate.AuthenticationResult?.AccessToken) {

    return {

      email: username,

      cognitoUsername: initiate.ChallengeParameters?.USERNAME ?? username,

      session: '',

      challengeName: 'CUSTOM_CHALLENGE',

      accessToken: initiate.AuthenticationResult.AccessToken,

    };

  }



  if (!initiate.ChallengeName || !initiate.Session) {

    throw new Error('Unexpected Cognito response');

  }



  // CreateAuthChallenge runs on InitiateAuth for this pool; OTP is sent without an empty ANSWER.

  return {

    email: username,

    cognitoUsername: initiate.ChallengeParameters?.USERNAME ?? username,

    session: initiate.Session,

    challengeName: initiate.ChallengeName,

  };

}



export async function verifyOtpAndGetAccessToken(

  config: CognitoOtpConfig,

  state: OtpChallengeState,

  otpCode: string

): Promise<string> {

  const client = new CognitoIdentityProviderClient({ region: config.region });

  const code = otpCode.trim();



  if (!code) {

    throw new Error('Verification code is required');

  }



  const cognitoUsername = state.cognitoUsername ?? state.email;

  let session = state.session;

  let challengeName = state.challengeName;



  for (let attempt = 0; attempt < 3; attempt += 1) {

    const response = await client.send(

      new RespondToAuthChallengeCommand({

        ClientId: config.clientId,

        ChallengeName: challengeName,

        Session: session,

        ChallengeResponses: {

          USERNAME: cognitoUsername,

          ANSWER: code,

        },

      })

    );



    if (response.AuthenticationResult?.AccessToken) {

      return response.AuthenticationResult.AccessToken;

    }



    if (!response.Session) {

      throw new Error('Verification failed');

    }



    session = response.Session;

    if (response.ChallengeName) challengeName = response.ChallengeName;

  }



  throw new Error('Verification failed');

}



export async function startOtpChallengeSafe(

  config: CognitoOtpConfig,

  email: string

): Promise<OtpChallengeState> {

  try {

    return await startOtpChallenge(config, email);

  } catch (error) {

    throw new Error(getErrorMessage(error));

  }

}



export async function verifyOtpSafe(

  config: CognitoOtpConfig,

  state: OtpChallengeState,

  otpCode: string

): Promise<string> {

  try {

    return await verifyOtpAndGetAccessToken(config, state, otpCode);

  } catch (error) {

    throw new Error(getErrorMessage(error));

  }

}


